import { Descriptions } from '@arco-design/web-react';

import { PROBE_TYPE_OPTIONS } from './constants';
import { hasMeaningfulHealthCheckConfig, healthCheckFromLivenessProbe, readHealthCheckFromFormValues } from './healthCheckForm';
import type { HealthCheckFormValues, ProbeType } from './types';

interface PreviewItem {
  label: string;
  value: string;
  span?: number;
}

function resolveProbeTypeLabel(probeType?: ProbeType | string): string {
  const normalized = String(probeType ?? '')
    .trim()
    .toLowerCase() as ProbeType;
  return (PROBE_TYPE_OPTIONS.find((item) => item.value === normalized)?.label ?? normalized) || '-';
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === '') return '-';
  return String(value);
}

/** 与 HealthCheckBlock 表单分行一致：整行字段 span=2，同行双字段各占一列 */
export function buildHealthCheckPreviewDescriptionData(healthCheck: HealthCheckFormValues): PreviewItem[] {
  const probeType = String(healthCheck.defaultProbeType ?? 'http')
    .trim()
    .toLowerCase() as ProbeType;
  const data: PreviewItem[] = [{ label: '默认探活类型', value: resolveProbeTypeLabel(probeType), span: 2 }];

  if (probeType === 'http' || probeType === 'tcp') {
    data.push({ label: '默认端口', value: formatValue(healthCheck.defaultPort), span: 2 });
  }
  if (probeType === 'http') {
    data.push(
      { label: '默认路径', value: formatValue(healthCheck.defaultPath) },
      { label: 'HTTP 请求方式', value: formatValue(healthCheck.httpMethod || 'GET') },
    );
  }
  if (probeType === 'exec') {
    data.push({ label: '默认执行命令', value: formatValue(healthCheck.defaultCommand), span: 2 });
  }

  data.push(
    { label: '启动等待时间 (秒)', value: formatValue(healthCheck.initialDelaySeconds) },
    { label: '检查间隔 (秒)', value: formatValue(healthCheck.periodSeconds) },
    { label: '超时时间 (秒)', value: formatValue(healthCheck.timeoutSeconds) },
    { label: '失败阈值', value: formatValue(healthCheck.failureThreshold) },
  );

  return data;
}

export interface HealthCheckPreviewSectionProps {
  healthCheck?: HealthCheckFormValues | null;
  /** 无表单探活时，从 K8s livenessProbe 回推 */
  livenessProbe?: Record<string, unknown> | null;
  /** 可选：整表 form values，优先读 `_containerDeployHealthCheck` */
  formValues?: Record<string, unknown>;
  emptyText?: string;
}

/** 确认预览：探活字段布局对齐配置态 HealthCheckBlock */
export function HealthCheckPreviewSection({
  healthCheck,
  livenessProbe,
  formValues,
  emptyText = '未配置',
}: HealthCheckPreviewSectionProps) {
  const resolved =
    healthCheck ?? (formValues ? readHealthCheckFromFormValues(formValues) : undefined) ?? healthCheckFromLivenessProbe(livenessProbe);

  if (!resolved || (!hasMeaningfulHealthCheckConfig(resolved) && !livenessProbe)) {
    return <Descriptions column={1} border data={[{ label: '-', value: emptyText }]} />;
  }

  return <Descriptions column={2} border data={buildHealthCheckPreviewDescriptionData(resolved)} />;
}
